import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, SelectPanel, type SelectPanelItemInput as ItemInput} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

import {type AgentModel, apiGetAgentModels} from '../../api/agent-models/api-get-agent-models'
import {WorkflowResources} from '../../strings'
import {buildPickerLabel} from './helpers/accessible-label'

const AUTO_MODEL_ID = '__auto__'

const AUTO_ITEM: ItemInput = {
  id: AUTO_MODEL_ID,
  text: WorkflowResources.assignCopilotModelAutoLabel,
}

type ModelResponse = {
  repoNameWithOwner: string
  models: Array<AgentModel>
}

type ModelPickerProps = {
  /** The `owner/repo` NWO of the selected repository. When undefined the picker is disabled. */
  repoNameWithOwner?: string
  /** Currently selected model ID, or undefined for "Auto". */
  selectedModelId?: string
  /** Called when the user selects a model. `undefined` means "Auto". */
  onModelSelected: (modelId: string | undefined) => void
  isEditing?: boolean
  headerDescription?: string
}

export const ModelPicker = ({
  repoNameWithOwner,
  selectedModelId,
  onModelSelected,
  isEditing,
  headerDescription,
}: ModelPickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [modelResponse, setModelResponse] = useState<ModelResponse>()

  // Fetch models whenever the selected repo changes
  useEffect(() => {
    if (!repoNameWithOwner) {
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const response = await apiGetAgentModels(repoNameWithOwner)
        if (!cancelled) {
          setModelResponse({repoNameWithOwner, models: response.models})
        }
      } catch {
        if (!cancelled) {
          setModelResponse({repoNameWithOwner, models: []})
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [repoNameWithOwner])

  const models = modelResponse && modelResponse.repoNameWithOwner === repoNameWithOwner ? modelResponse.models : []
  const loading = Boolean(repoNameWithOwner && modelResponse?.repoNameWithOwner !== repoNameWithOwner)

  const modelItems = models.map<ItemInput>(model => ({
    id: model.id,
    text: model.name,
    description: model.vendor,
  }))
  const items: Array<ItemInput> = [AUTO_ITEM, ...modelItems]

  const filteredItems = items.filter(item => item.text?.toLowerCase().includes(filter.toLowerCase()))

  const selected = selectedModelId ? items.find(item => item.id === selectedModelId) : AUTO_ITEM
  const selectedText = selected?.text ?? selectedModelId ?? WorkflowResources.assignCopilotModelAutoLabel

  const onSelectedChange = useCallback(
    (selectedItem: ItemInput | undefined) => {
      if (!selectedItem || selectedItem.id === AUTO_MODEL_ID) {
        onModelSelected(undefined)
      } else {
        onModelSelected(selectedItem.id as string)
      }
    },
    [onModelSelected],
  )

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const isDisabled = !isEditing || !repoNameWithOwner

  const label = buildPickerLabel(headerDescription, selectedText)

  return (
    <SelectPanel
      placeholderText={WorkflowResources.assignCopilotModelFilterPlaceholder}
      open={isOpen}
      onOpenChange={setIsOpen}
      selected={selected}
      onFilterChange={setFilter}
      items={filteredItems}
      loading={loading}
      onSelectedChange={onSelectedChange}
      renderAnchor={({children, ...anchorProps}) => (
        <Button
          disabled={isDisabled}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          {...testIdProps('workflow-auto-assign-agent-model')}
          aria-label={label}
        >
          {children || selectedText}
        </Button>
      )}
      overlayProps={{
        width: 'small',
        onMouseDown: e => e.stopPropagation(),
        height: 'auto',
        onClickOutside: onClose,
        ...testIdProps('workflow-auto-assign-agent-model-panel'),
      }}
    />
  )
}

ModelPicker.displayName = 'ModelPicker'
